import { useMemo } from "react";

import { Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import {
  buildOverlapMeetingList,
  getUserBufferSetting,
} from "@/components/pieces/coaching/meetings/utils";
import { LinkButton } from "@/components/pieces/link/LinkButton";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { UpcomingMeeting } from "@/types/meetings";
import { CoachingCurrentUser, CoachingCustomer, RoleView } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";

import { MeetingBasics } from "./MeetingBasics";

type Meeting = UpcomingMeeting & {
  backgroundColor?: string;
};
interface UpcomingMeetingsInnerProps {
  meetings: Meeting[];
  sessionsLink?: string;
  customerId?: string;
  view?: RoleView;
  title?: React.ReactNode;
}

function UpcomingMeetingsInner({
  meetings,
  sessionsLink,
  customerId,
  view = "coach",
  title,
}: UpcomingMeetingsInnerProps) {
  return (
    <>
      <Flex pb={3} alignItems="center" justifyContent="space-between">
        <Box fontSize={20} fontWeight={600}>
          {title}
        </Box>
        <Box>
          {sessionsLink && (
            <LinkButton to={sessionsLink}>
              <Media at="xs">
                <Icon icon="history" color={SUBTEXT_COLOR} size={20} />
              </Media>
              <Media greaterThan="xs">History</Media>
            </LinkButton>
          )}
        </Box>
      </Flex>
      {meetings.map((meeting, index) => (
        <Box
          key={meeting.id}
          className="round-corners bas"
          mt={index > 0 ? 22 : 0}
          px={24}
          py={3}
          css={{ backgroundColor: meeting.backgroundColor }}
        >
          <MeetingBasics
            meeting={meeting}
            showMessageButton={true}
            chatChannelId={
              "chatChannelId" in meeting ? meeting.chatChannelId : undefined
            }
            showMessageCount={true}
            view={view}
            customerId={customerId}
          />
        </Box>
      ))}
    </>
  );
}

type User = (CoachingCurrentUser | CoachingCustomer) & {
  isAgent?: boolean;
  agentProfile?: CoachingCurrentUser["agentProfile"];
};

interface UpcomingMeetingsProps {
  user: User;
  customerId?: string;
  sessionsLink?: string;
  mt?: number;
}

export function UpcomingMeetings({
  user,
  customerId,
  sessionsLink = "/sessions",
  mt = 3,
}: UpcomingMeetingsProps) {
  const upcomingMeetings: UpcomingMeeting[] = ensureNonNullArray(
    user?.upcomingMeetings,
  );

  const bookingRequestMeetings = ensureNonNullArray(
    user?.bookingRequestMeetings,
  );

  const upcomingMeetingList = useMemo(() => {
    const { beforeBuffer, afterBuffer } = getUserBufferSetting(user);
    const canceledMeetings = upcomingMeetings.filter(
      (meeting) => meeting.canceledAt,
    );

    const normalMeetings: Meeting[] = upcomingMeetings.filter(
      (meeting) => !meeting.canceledAt,
    );

    const overlapMeetingList = buildOverlapMeetingList({
      meetingLists: normalMeetings,
      beforeBuffer,
      afterBuffer,
    });

    return overlapMeetingList
      .concat(canceledMeetings)
      .sort((meetingA, meetingB) => {
        if (!meetingA.eventAt || !meetingB.eventAt) return 0;
        return meetingA.eventAt > meetingB.eventAt ? 1 : -1;
      });
  }, [user]);

  const hasUpcoming = upcomingMeetings.length > 0;
  const hasBookingRequest = bookingRequestMeetings.length > 0;
  const hasMeeting = hasUpcoming || hasBookingRequest;

  if (!hasMeeting) return null;

  return (
    <Box className={CARD_CLASSES} backgroundColor="#FFF" mt={mt} p={24}>
      {hasUpcoming && (
        <UpcomingMeetingsInner
          meetings={upcomingMeetingList}
          sessionsLink={sessionsLink}
          customerId={customerId}
          title="Upcoming Sessions"
        />
      )}
      {hasBookingRequest && (
        <UpcomingMeetingsInner
          meetings={bookingRequestMeetings}
          sessionsLink={!hasUpcoming ? sessionsLink : undefined}
          customerId={customerId}
          title={<Box mt={3}>Pending Booking Requests</Box>}
        />
      )}
    </Box>
  );
}
